import { type ColumnDef, numericColumnComparator, toBig } from '@talos/kyoko';
import type { ColDef, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { CARE_ORDER_CUSTOM_COLUMN_IDS } from '../careOrderColumnIds';
import { type CareOrderBlotterEntity, isCareOrderRow, isQuoteRow } from '../types';

export const unitsTradedColumn: ColumnDef<CareOrderBlotterEntity> = {
  type: 'custom',
  id: CARE_ORDER_CUSTOM_COLUMN_IDS.UnitsTraded,
  title: 'Units Traded',
  hide: true,
  width: 100,
  description: 'The units traded (if applicable). Rounded to 2dp.',
  params: {
    headerClass: 'ag-right-aligned-header',
    type: 'numericColumn',
    valueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>): string | undefined => {
      const node = params.node;
      if (!node) {
        return undefined;
      }

      return getShareOfCareOrderUnits(node);
    },
    valueFormatter: ({ value }: ValueFormatterParams<CareOrderBlotterEntity, string | undefined>): string => {
      const valueBig = toBig(value);
      if (!valueBig) {
        return '';
      }

      return valueBig.toFixed(2);
    },
    comparator: numericColumnComparator,
  } satisfies ColDef<CareOrderBlotterEntity>,
};

/**
 * Pass in a child care order node and if this is a child node to a parent care order in units space,
 * will return a string like "3.12" representing how many of the parent order's units this child order filled.
 */
function getShareOfCareOrderUnits(node: IRowNode<CareOrderBlotterEntity>): string | undefined {
  const data = node.data;
  if (!data) {
    return undefined;
  }

  let orderQty: string | undefined = undefined;
  let cumQty: string | undefined = undefined;
  let units: string | undefined = undefined;

  if (isCareOrderRow(data)) {
    if (data.isQtyInUnits) {
      // If the CO's top level properties are directly in units, use that
      return data.CumQty;
    }

    orderQty = data.OrderQty;
    cumQty = data.CumQty;
    units = data.Annotations?.BasketOrder?.BasketOrderQty;
  } else if (isQuoteRow(data)) {
    cumQty = data.TradedQty;

    if (data.isQtyInUnits) {
      return data.TradedQty;
    }

    // Else the quote's qty is not in units, so we are in the old codepath where CO is in units, but
    // quote's qty is in base. The quote's parent CO has BasketOrderQty in its annotations which we use to compute units in this case
    const parentData = node.parent?.data;
    if (!parentData || !isCareOrderRow(parentData)) {
      // ParentData has to be defined, and it has to be a CareOrderRow.
      return undefined;
    }

    orderQty = parentData.OrderQty;
    units = parentData.Annotations?.BasketOrder?.BasketOrderQty;
  }

  const unitsBig = toBig(units);
  if (!unitsBig || unitsBig.eq(0)) {
    return undefined;
  }

  const orderQtyBig = toBig(orderQty);
  const cumQtyBig = toBig(cumQty);

  if (!orderQtyBig || !cumQtyBig) {
    return undefined;
  }

  if (orderQtyBig.eq(0) || cumQtyBig.eq(0)) {
    return '0';
  }

  const share = cumQtyBig.div(orderQtyBig);
  const shareInUnits = share.times(unitsBig);
  return shareInUnits.toFixed();
}
